import { create } from "zustand";
import { nanoid } from "nanoid";
import { persist, createJSONStorage } from "zustand/middleware";

export type WindowId = string;

export type Bounds = {
  x: number;
  y: number;
  width: number | string; // react-rnd permite string en width/height
  height: number | string;
};

export type WinState = Bounds & {
  id: WindowId;
  title: React.ReactNode;
  identifier?: string; // <- opcional
  subtitle?: string; // <- para mostrar información adicional (ej: teléfono)
  metadata?: {
    // <- metadata adicional (ej: nombre del cliente)
    name?: string;
    phone?: string;
  };
  z: number;
  minimized: boolean;
  maximized: boolean;
  last?: { x: number; y: number; width: number; height: number };
  content?: React.ReactNode;
  noHeader?: boolean;
};

type Placement =
  | "top-right"
  | "bottom-right"
  | "right-cascade"
  | "top-left"
  | "bottom-left"
  | "left-cascade";

type OpenParams = Partial<Bounds> & {
  id?: WindowId;
  title: React.ReactNode;
  content?: React.ReactNode;
  identifier?: string;
  subtitle?: string; // <- para mostrar información adicional (ej: teléfono)
  metadata?: {
    // <- metadata adicional (ej: nombre del cliente)
    name?: string;
    phone?: string;
  };
  placement?: Placement; // <- NUEVO
  offsetStep?: number; // <- NUEVO (px para cascada)
  noHeader?: boolean;
};

type WinStore = {
  windows: WinState[];
  zTop: number;

  open: (params: OpenParams) => WindowId;
  close: (id: WindowId) => void;
  closeAll: () => void;

  focus: (id: WindowId) => void;
  setPos: (id: WindowId, x: number, y: number) => void;
  setSize: (id: WindowId, width: number, height: number) => void;

  minimize: (id: WindowId) => void;
  toggleMinimize: (id: WindowId) => void;

  maximize: (id: WindowId) => void;
  toggleMaximize: (id: WindowId) => void;

  restore: (id: WindowId) => void;
};

const DEFAULT_BOUNDS: Bounds = {
  x: 80,
  y: 60,
  width: 420,
  height: 320
};

const PADDING = 16;
const TASKBAR = 44;

function num(n: number | string | undefined, fallback: number) {
  if (typeof n === "number") return n;
  const v = Number(n);
  return Number.isFinite(v) ? v : fallback;
}

function computeXY(
  s: WinStore,
  width: number,
  height: number,
  placement: Placement,
  step: number
) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;

  const isLeft =
    placement === "top-left" ||
    placement === "bottom-left" ||
    placement === "left-cascade";

  const baseX = isLeft
    ? Math.max(0, PADDING)
    : Math.max(0, vw - width - PADDING);

  const topY = PADDING;
  const bottomY = Math.max(0, vh - height - TASKBAR - PADDING);

  // top-* por defecto, salvo que sea bottom-*
  let y =
    placement === "bottom-right" || placement === "bottom-left"
      ? bottomY
      : topY;

  // *-cascade respeta la misma lógica de “k pasos”
  if (placement === "right-cascade" || placement === "left-cascade") {
    const n = s.windows.filter((w) => !w.minimized).length;
    const k = n % 8; // 8 pasos y reinicia
    y = Math.min(bottomY, topY + k * step);
  }

  return { x: baseX, y };
}

export const useWinStore = create<WinStore>()(
  persist(
    (set, get) => ({
      windows: [],
      zTop: 1000,

      closeAll: () => set({ windows: [], zTop: 1000 }),

      open: (params) => {
        const id = params.id ?? nanoid(8);

        const w = num(params.width ?? DEFAULT_BOUNDS.width, 420);
        // Calcular altura máxima: viewport menos taskbar y padding
        // Para modales que necesitan altura completa, usar menos espacio (solo taskbar + padding)
        const vh = typeof window !== "undefined" ? window.innerHeight : 800;
        const maxHeight = vh - TASKBAR - PADDING;
        const requestedHeight = num(
          params.height ?? DEFAULT_BOUNDS.height,
          320
        );
        const h = Math.min(requestedHeight, maxHeight);

        let x = typeof params.x === "number" ? params.x : undefined;
        let y = typeof params.y === "number" ? params.y : undefined;

        const placement: Placement = params.placement ?? "right-cascade";
        const step = params.offsetStep ?? 28;

        if (x == null || y == null) {
          const p = computeXY(get(), w, h, placement, step);
          x = x ?? p.x;
          y = y ?? p.y;
        }

        const z = get().zTop + 1;

        set((s) => ({
          zTop: z,
          windows: [
            ...s.windows,
            {
              id,
              title: params.title,
              content: params.content,
              identifier: params.identifier,
              subtitle: params.subtitle,
              metadata: params.metadata,
              x: Number(x),
              y: Number(y),
              width: w,
              height: h,
              z,
              minimized: false,
              maximized: false,
              noHeader: params.noHeader
            }
          ]
        }));

        return id;
      },

      close: (id) =>
        set((s) => ({ windows: s.windows.filter((w) => w.id !== id) })),

      focus: (id) =>
        set((s) => {
          const z = s.zTop + 1;
          return {
            zTop: z,
            windows: s.windows.map((w) => (w.id === id ? { ...w, z } : w))
          };
        }),

      setPos: (id, x, y) =>
        set((s) => ({
          windows: s.windows.map((w) => (w.id === id ? { ...w, x, y } : w))
        })),

      setSize: (id, width, height) =>
        set((s) => ({
          windows: s.windows.map((w) =>
            w.id === id ? { ...w, width, height } : w
          )
        })),

      minimize: (id) =>
        set((s) => ({
          windows: s.windows.map((w) =>
            w.id === id
              ? {
                  ...w,
                  minimized: true,
                  maximized: false,
                  last: !w.maximized
                    ? {
                        x: w.x,
                        y: w.y,
                        width: num(w.width, 420),
                        height: num(w.height, 320)
                      }
                    : w.last
                }
              : w
          )
        })),

      toggleMinimize: (id) =>
        set((s) => ({
          windows: s.windows.map((w) =>
            w.id === id
              ? {
                  ...w,
                  minimized: !w.minimized,
                  maximized: w.minimized ? false : w.maximized
                }
              : w
          )
        })),

      maximize: (id) =>
        set((s) => {
          const vw = window.innerWidth;
          const vh = window.innerHeight;
          return {
            windows: s.windows.map((w) =>
              w.id === id
                ? {
                    ...w,
                    last: !w.maximized
                      ? {
                          x: w.x,
                          y: w.y,
                          width: num(w.width, 420),
                          height: num(w.height, 320)
                        }
                      : w.last,
                    x: 0,
                    y: 0,
                    width: vw,
                    height: vh - TASKBAR,
                    minimized: false,
                    maximized: true
                  }
                : w
            )
          };
        }),

      toggleMaximize: (id) => {
        const { windows } = get();
        const w = windows.find((x) => x.id === id);
        if (!w) return;

        if (w.maximized) {
          get().restore(id);
        } else {
          get().maximize(id);
        }
      },

      restore: (id) =>
        set((s) => ({
          windows: s.windows.map((w) =>
            w.id === id
              ? {
                  ...w,
                  minimized: false,
                  maximized: false,
                  x: w.last?.x ?? w.x,
                  y: w.last?.y ?? w.y,
                  width: w.last?.width ?? w.width,
                  height: w.last?.height ?? w.height
                }
              : w
          )
        }))
    }),
    {
      name: "wm",
      version: 2,
      storage: createJSONStorage(() => localStorage),
      partialize: () => ({}),
      migrate: (persisted, fromVersion) => {
        return { windows: [], zTop: 1000 };
      }
    }
  )
);
